import './floating-buttons.scss';
import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useState } from 'react';

import { ReactComponent as NaverBlogIcon } from '@/assets/icons/svg/naver-blog-pc.svg';
import { ReactComponent as DevelloIcon } from '@/assets/icons/svg/devello-pc.svg';
import { ReactComponent as InstagramIcon } from '@/assets/icons/svg/instagram-pc.svg';
import { ReactComponent as YoutubeIcon } from '@/assets/icons/svg/youtube-pc.svg';
import { ReactComponent as NaverMapIcon } from '@/assets/icons/svg/naver-map-pc.svg';
import { ReactComponent as MenuIcon } from '@/assets/icons/svg/common/floating-plus.svg';
import {
	유튜브_링크,
	블로그_링크,
	인스타그램_링크,
	네이버_지도_링크
} from '@/constants/links';
import { useRouter } from '@/hooks/useRouter';
import paths from '@/routes/paths';
import useDeviceSize from '@/hooks/useDeviceSize';

const AUTO_CLOSE_TIME = 4000; // 4초뒤 닫힘

const FloatingButtons = () => {
	const { deviceSize } = useDeviceSize();
	const [isOpen, setIsOpen] = useState(false);
	const router = useRouter();

	const handleLinkClick = (link: string) => {
		window.open(link, '_blank');
	};

	const isMobile = deviceSize === 'mobile';

	const buttonVariants = {
		hidden: (index: number) => ({
			y: 0,
			opacity: 0,
			transition: {
				duration: 0.3,
				delay: 0.1
			}
		}),
		visible: (index: number) => ({
			y: -45 * (index + 1),
			opacity: 1,
			transition: {
				duration: 0.3,
				delay: 0.1
			}
		})
	};

	const buttons = [
		{
			icon: <DevelloIcon className="devello" />,
			onClick: () => router.push(paths.inquiry),
			ariaLabel: '문의하기'
		},
		{
			icon: <NaverBlogIcon className="naver-blog" />,
			onClick: () => handleLinkClick(블로그_링크),
			ariaLabel: '블로그로 이동'
		},
		// {
		// 	icon: <NaverMapIcon className="naver-map" />,
		// 	onClick: () => handleLinkClick(네이버_지도_링크)
		// },
		{
			icon: <YoutubeIcon className="youtube" />,
			onClick: () => handleLinkClick(유튜브_링크),
			ariaLabel: '유튜브로 이동'
		},
		{
			icon: <InstagramIcon className="instagram" />,
			onClick: () => handleLinkClick(인스타그램_링크),
			ariaLabel: '인스타그램으로 이동'
		}
	];

	// 모바일에서 자동 닫힘 타이머 추가
	useEffect(() => {
		let timer: NodeJS.Timeout;

		if (isOpen && deviceSize === 'mobile') {
			timer = setTimeout(() => {
				setIsOpen(false);
			}, AUTO_CLOSE_TIME);
		}

		// 컴포넌트가 언마운트되거나 isOpen이 변경되기 전에 타이머 정리
		return () => {
			if (timer) {
				clearTimeout(timer);
			}
		};
	}, [isOpen, deviceSize]);

	return (
		<div className="floating-buttons-wrapper">
			{!isMobile ? (
				buttons.map((button, index) => (
					<button
						key={index}
						className="circle-button"
						onClick={button.onClick}
					>
						{button.icon}
					</button>
				))
			) : (
				<>
					<AnimatePresence>
						{isOpen &&
							buttons.reverse().map((button, index) => (
								<motion.button
									key={index}
									className="circle-button"
									custom={index} // variants로 전달함.
									variants={buttonVariants} // custom에서 전달받음.
									initial="hidden"
									animate="visible"
									exit="hidden"
									onClick={button.onClick}
									aria-label={button.ariaLabel}
								>
									{button.icon}
								</motion.button>
							))}
					</AnimatePresence>
					<motion.button
						className="circle-button toggle-button"
						onClick={() => setIsOpen(!isOpen)}
						animate={{ rotate: isOpen ? 45 : 0 }}
						aria-label="메뉴 열기/닫기"
					>
						<MenuIcon className="menu-icon" />
					</motion.button>
				</>
			)}
		</div>
	);
};

export default FloatingButtons;
